'use client';

import React, { useMemo } from 'react';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import {
  useGetCodeByNameQuery,
} from 'src/api/redux';
import { useGetCodeSetVolumeByCodePaginatedInfiniteQuery } from 'src/api/redux/providers/providersApi';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { applyDisplayMode } from 'src/components/Profiles/shared/widgets/utils';
import { useInfiniteScroll } from 'src/hooks/common/useInfiniteScroll';
import MonthlyAvgBadge from 'src/components/Profiles/shared/widgets/MonthlyAvgBadge';

const CompactCodeSetVolumeByCode: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();

  const entityId = entityDetails?.id;
  const codeSetId = widgetConfig?.widget?.code_set?.id;
  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;

  const {
    data: paginatedData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetCodeSetVolumeByCodePaginatedInfiniteQuery(
    {
      entityId: String(entityId ?? ''),
      codeSetId: codeSetId!,
      minYear,
      maxYear,
    },
    { skip: !entityId || !codeSetId }
  );

  const rows = useMemo(
    () => paginatedData?.pages?.flatMap((page: any) => page.results) ?? [],
    [paginatedData]
  );

  const { fetchTriggerRef } = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetching: isLoading || !!isFetchingNextPage,
    fetchNextPage,
  });

  const codeNames = useMemo(() => {
    return rows.map((item) => item.code_name);
  }, [rows]);

  const { data: codeDetailsData } = useGetCodeByNameQuery(
    {
      codeIds: codeNames,
    },
    {
      skip: codeNames.length === 0,
    }
  );

  const descriptionMap = useMemo(() => {
    if (!codeDetailsData?.results) return {};
    return codeDetailsData.results.reduce(
      (
        acc: Record<string, string>,
        code: { code_name?: string; description?: string }
      ) => {
        if (code.code_name && code.description) {
          acc[code.code_name] = code.description;
        }
        return acc;
      },
      {}
    );
  }, [codeDetailsData]);

  const displayMode = widgetConfig?.widget?.display_mode;
  const isMonthlyAvg = displayMode === 'monthly_average';
  const volumeType = widgetConfig?.widget?.volume_type ?? 'claims';

  const heroTotal = useMemo(() => {
    if (!rows.length) return null;
    const key = volumeType === 'patients' ? 'patients' : 'claims';
    const total = rows.reduce((sum, item) => sum + item[key], 0);
    return applyDisplayMode(total, displayMode);
  }, [rows, volumeType, displayMode]);

  const heroLabel = volumeType === 'patients' ? 'Patients' : 'Claims';

  const codeSetName = widgetConfig?.widget?.code_set?.label;
  const fallbackTitle = codeSetName
    ? `Code Set Volume by Code - ${codeSetName}`
    : 'Code Set Volume by Code';

  return (
    <Card
      className="p-4 flex flex-col gap-3 bg-white h-full"
      data-testid="compact-code-set-volume-by-code"
      role="region"
      aria-label={fallbackTitle}
    >
      <WidgetHeader
        fallbackTitle={fallbackTitle}
        trailing={isMonthlyAvg ? <MonthlyAvgBadge /> : undefined}
      />

      {heroTotal != null && !isLoading && !widgetConfig?.isLoading && (
        <div
          className="flex items-baseline gap-2"
          data-testid="compact-code-set-volume-by-code-hero"
          aria-label={`Total ${heroLabel}: ${handleNumericValue(heroTotal)}`}
        >
          <p
            className="text-4xl font-bold text-sky-600 tabular-nums"
            data-testid="compact-code-set-volume-by-code-hero-value"
          >
            {handleNumericValue(heroTotal)}
          </p>
          <p
            className="text-base text-neutral-500"
            data-testid="compact-code-set-volume-by-code-hero-label"
          >
            {heroLabel}
          </p>
        </div>
      )}

      <div className="flex-1 min-h-0 max-h-72 overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:rounded-3xl">
        {isLoading || widgetConfig?.isLoading ? (
          <WidgetLoading data-testid="compact-code-set-volume-by-code-loading" />
        ) : rows.length === 0 ? (
          <WidgetEmpty data-testid="compact-code-set-volume-by-code-empty" variant="full" />
        ) : (
          <div
            className="flex flex-col"
            role="list"
            aria-label={fallbackTitle}
            data-testid="compact-code-set-volume-by-code-table"
          >
            <div className="flex items-center justify-end gap-2 px-1 pb-1 border-b border-neutral-100 sticky top-0 bg-white z-10">
              <span className="text-xs font-semibold text-primary min-w-16 text-end">
                {heroLabel}
              </span>
            </div>
            {rows.map((item) => (
              <div
                key={item.code_name}
                className="flex items-center justify-between gap-2 py-3 border-b border-neutral-100 last:border-b-0"
                role="listitem"
                data-testid="compact-code-set-volume-by-code-row"
              >
                <div className="flex flex-col min-w-0 flex-1">
                  <span
                    className="text-sm font-bold text-primary truncate"
                    title={item.code_name}
                  >
                    {item.code_name}
                  </span>
                  {descriptionMap[item.code_name] && (
                    <span
                      className="text-xs text-secondary truncate"
                      title={descriptionMap[item.code_name]}
                      data-testid="compact-code-set-volume-by-code-description"
                    >
                      {descriptionMap[item.code_name]}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-end px-2 py-1 min-w-16 flex-shrink-0">
                  <span className="text-sm font-semibold text-teal-900 tabular-nums">
                    {applyDisplayMode(
                      volumeType === 'patients' ? item.patients : item.claims,
                      displayMode
                    ).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}

            {/* Infinite scroll sentinel + spinner */}
            <div
              ref={fetchTriggerRef}
              className="h-1"
              aria-hidden="true"
              data-testid="compact-code-set-volume-by-code-scroll-trigger"
            />
            {isFetchingNextPage && (
              <WidgetLoading data-testid="compact-code-set-volume-by-code-fetching-next" />
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default CompactCodeSetVolumeByCode;
